<template>
  <div
    class="flex flex-col bg-white"
    :class="flush
      ? 'h-full'
      : 'overflow-hidden rounded-xl shadow-[0px_6px_15px_-2px_rgba(16,24,40,0.08)]'"
  >
    <!-- Header -->
    <div v-if="showHeader" class="px-4 py-6 lg:px-6">
      <h2 class="text-[32px] font-medium leading-10 tracking-[-0.02em] text-code-medical-center lg:text-[36px] lg:leading-[44px]">
        {{ $t('booking.heading') }}
      </h2>
    </div>

    <template v-if="bookableServices.length">
      <!-- Service select band -->
      <div
        class="border-neutral-200 bg-neutral-50 px-4 lg:px-6"
        :class="[
          showHeader ? 'border-y' : 'border-b',
          compact ? 'py-4' : 'py-6',
        ]"
      >
        <div class="flex flex-col gap-2">
          <label class="text-sm font-medium leading-4 text-neutral-900">
            {{ $t('booking.select_service_kind') }}
          </label>

          <div ref="serviceSelectRoot" class="relative">
            <button
              ref="serviceButton"
              type="button"
              class="flex h-12 w-full items-center gap-3 rounded-lg border border-neutral-400 bg-white px-3 text-left backdrop-blur-xs"
              :aria-expanded="serviceDropdownOpen"
              aria-haspopup="listbox"
              @click="serviceDropdownOpen = !serviceDropdownOpen"
            >
              <span class="min-w-0 flex-1 truncate text-sm leading-4 text-neutral-900">
                {{ selectedService?.name }}
              </span>
              <span
                v-if="selectedServiceMeta"
                class="shrink-0 text-sm font-semibold leading-4 text-green-600"
              >
                {{ selectedServiceMeta }}
              </span>
              <Icon
                name="cheveron-down"
                class="size-5 shrink-0 fill-current text-neutral-900 transition-transform"
                :class="{ 'rotate-180': serviceDropdownOpen }"
                aria-hidden="true"
              />
            </button>

            <teleport to="body">
              <ul
                v-if="serviceDropdownOpen"
                ref="serviceDropdown"
                class="z-10050 max-h-60 overflow-auto rounded-lg border border-neutral-200 bg-white py-1 shadow-lg"
                :style="serviceDropdownStyle"
                role="listbox"
              >
                <li
                  v-for="service in bookableServices"
                  :key="service.remote_uuid"
                  role="option"
                  :aria-selected="service.remote_uuid === selectedServiceUuid"
                >
                  <button
                    type="button"
                    class="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-neutral-50"
                    :class="{ 'bg-sky-50': service.remote_uuid === selectedServiceUuid }"
                    @click="selectService(service.remote_uuid)"
                  >
                    <span class="min-w-0 flex-1 truncate text-sm leading-4 text-neutral-900">
                      {{ service.name }}
                    </span>
                    <span
                      v-if="formatServiceMeta(service)"
                      class="shrink-0 text-sm font-semibold leading-4 text-green-600"
                    >
                      {{ formatServiceMeta(service) }}
                    </span>
                  </button>
                </li>
              </ul>
            </teleport>
          </div>
        </div>
      </div>

      <!-- Calendar body -->
      <div
        class="relative lg:px-6"
        :class="compact ? 'px-5 py-4' : 'px-4 py-6'"
      >
        <div
          v-if="loading"
          class="py-16 text-center text-sm leading-5 text-neutral-500"
        >
          {{ $t('booking.loading_slots') }}
        </div>

        <div
          v-else-if="loadError"
          class="py-16 text-center text-sm leading-5 text-neutral-500"
        >
          {{ loadError }}
        </div>

        <div
          v-else-if="!hasAnyPeriods && interestSubmitted"
          class="flex flex-col items-center px-1 pt-0 text-center"
        >
          <svg
            class="size-12 shrink-0 text-sky-700"
            viewBox="0 0 48 48"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M10.4 27.15 17.9 34.65 37.6 14.95"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <div class="mt-6 w-full text-base font-normal leading-6 text-neutral-900">
            <p>{{ $t('booking.interest_thanks_title') }}</p>
            <p>{{ $t('booking.interest_thanks_body') }}</p>
          </div>
        </div>

        <div
          v-else-if="!hasAnyPeriods"
          class="flex flex-col gap-6"
        >
          <p class="text-base font-normal leading-6 text-neutral-900">
            {{ $t('booking.interest_prompt') }}
          </p>

          <form
            class="relative flex flex-col gap-3 sm:flex-row sm:items-end sm:gap-6"
            @submit.prevent="submitInterest"
          >
            <!-- Honeypot: hidden from humans, bots that autofill it get a fake success. -->
            <div
              aria-hidden="true"
              class="pointer-events-none absolute -left-[9999px] h-0 w-0 overflow-hidden opacity-0"
            >
              <label for="booking-interest-company">Company website</label>
              <input
                id="booking-interest-company"
                v-model="interestHoneypot"
                type="text"
                name="company_website"
                tabindex="-1"
                autocomplete="off"
              >
            </div>

            <div class="flex min-w-0 flex-1 flex-col gap-2">
              <label
                for="booking-interest-contact"
                class="text-sm font-medium leading-4 text-neutral-900"
              >
                {{ $t('booking.interest_contact_label') }}
              </label>
              <input
                id="booking-interest-contact"
                v-model="interestContact"
                type="text"
                autocomplete="email tel"
                class="h-12 w-full rounded-lg border border-neutral-200 bg-white px-3 text-sm leading-4 text-neutral-900 shadow-[0px_1px_1px_rgba(16,24,40,0.04)] outline-none focus:border-code-medical-center focus:ring-1 focus:ring-code-medical-center disabled:opacity-60"
                :disabled="interestSubmitting"
              >
              <p v-if="interestError" class="text-sm text-red-600">
                {{ interestError }}
              </p>
            </div>

            <button
              type="submit"
              class="inline-flex h-12 shrink-0 items-center justify-center rounded-lg border border-code-medical-center bg-white px-5 text-base font-semibold leading-[18px] text-code-medical-center shadow-[0px_2px_6px_0px_rgba(16,24,40,0.06)] transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-60"
              :disabled="interestSubmitting"
            >
              {{ $t('booking.interest_submit') }}
            </button>
          </form>
        </div>

        <template v-else>
          <!-- Fixed w-16 columns (same strip as ServiceSlotsCalendar) — never 1fr stretch -->
          <div ref="daysScroll" class="overflow-x-auto">
            <div class="flex min-w-min items-start justify-between gap-2 px-1 pt-1">
              <button
                type="button"
                class="flex size-11 shrink-0 items-center justify-center self-start rounded-full border-2 p-2.5 transition-colors"
                :class="canGoBack
                  ? 'border-code-medical-center-nav text-code-medical-center-nav opacity-90 hover:bg-neutral-100'
                  : 'cursor-not-allowed border-neutral-200 text-neutral-200'"
                :disabled="!canGoBack"
                :aria-label="$t('booking.prev_days')"
                @click="shiftDays(-effectiveDayCount)"
              >
                <svg class="size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15 6l-6 6 6 6" />
                </svg>
              </button>

              <div class="flex flex-1 items-start justify-center gap-6 lg:justify-start">
                <div
                  v-for="day in visibleDays"
                  :key="day.isoDate"
                  class="flex w-16 shrink-0 flex-col items-stretch gap-4"
                >
                  <div class="flex flex-col items-center gap-1 text-center leading-tight">
                    <span class="whitespace-nowrap text-base font-semibold leading-[18px] text-code-medical-center-nav">
                      {{ day.label }}
                    </span>
                    <span class="text-sm font-normal leading-4 text-neutral-500">
                      {{ day.dateLabel }}
                    </span>
                  </div>
                  <div class="flex flex-col items-stretch gap-3">
                    <BookingSlotButton
                      v-for="period in visiblePeriods(day.isoDate)"
                      :key="`${day.isoDate}-${period}`"
                      @click="goToRegistration(day.isoDate, period)"
                    >
                      {{ period }}
                    </BookingSlotButton>
                  </div>
                </div>
              </div>

              <button
                type="button"
                class="flex size-11 shrink-0 items-center justify-center self-start rounded-full border-2 p-2.5 transition-colors"
                :class="canGoForward
                  ? 'border-code-medical-center-nav text-code-medical-center-nav opacity-90 hover:bg-neutral-100'
                  : 'cursor-not-allowed border-neutral-200 text-neutral-200'"
                :disabled="!canGoForward"
                :aria-label="$t('booking.next_days')"
                @click="shiftDays(effectiveDayCount)"
              >
                <svg class="size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 6l6 6-6 6" />
                </svg>
              </button>
            </div>
          </div>
        </template>
      </div>

      <!-- Footer -->
      <div
        v-if="!loading && !loadError && hasMorePeriods"
        class="border-t border-neutral-200 px-4 py-3 lg:px-6"
      >
        <button
          type="button"
          class="mx-auto flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium leading-4 text-sky-700 hover:text-sky-800"
          @click="showAllPeriods = !showAllPeriods"
        >
          {{ showAllPeriods ? $t('booking.show_less_hours') : $t('booking.show_more_hours') }}
          <Icon
            name="cheveron-down"
            class="size-5 fill-current transition-transform"
            :class="{ 'rotate-180': showAllPeriods }"
            aria-hidden="true"
          />
        </button>
      </div>
    </template>

    <div
      v-else
      class="border-t border-neutral-200 px-4 py-10 text-center text-sm leading-5 text-neutral-500 lg:px-6"
    >
      {{ $t('booking.no_online_services') }}
    </div>
  </div>
</template>

<script>
import { router } from '@inertiajs/vue3';
import { autoUpdate, computePosition, flip, offset, shift } from '@floating-ui/dom';
import { extractDefaultServiceUuid, formatServiceMeta } from '@/utils/serviceMeta';
import {
  DEFAULT_MOBILE_DAY_COUNT,
  VISIBLE_DAY_COUNT,
  resolveDayColumns,
} from '@/utils/bookingDayColumns';
import BookingSlotButton from '@/Shared/BookingSlotButton.vue';
import Icon from '@/Shared/Icon.vue';

const SLOT_DAYS = 60;
const INITIAL_PERIOD_COUNT = 5;

function pad(value) {
  return String(value).padStart(2, '0');
}

function toIsoDate(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function formatStartDate(date) {
  return `${toIsoDate(date)} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function startOfDay(date) {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

export default {
  name: 'SpecialistBookingPanel',
  components: { BookingSlotButton, Icon },
  props: {
    specialistSlug: {
      type: String,
      required: true,
    },
    bookableServices: {
      type: Array,
      default: () => [],
    },
    defaultServices: {
      type: Array,
      default: () => [],
    },
    modelValue: {
      type: String,
      default: null,
    },
    // Renders the big "Umów wizytę" heading. Disabled when embedding the
    // calendar inside another card (e.g. the /szukaj results list).
    showHeader: {
      type: Boolean,
      default: true,
    },
    // Strips the panel's own rounded corners/shadow so it fills a parent card
    // column instead of floating as a standalone card.
    flush: {
      type: Boolean,
      default: false,
    },
    // Desktop day-column count (lg+). Below lg, columns are fitted to width.
    visibleDayCount: {
      type: Number,
      default: VISIBLE_DAY_COUNT,
    },
    // Tighter spacing for narrow/mobile layouts (e.g. the booking slide-over).
    compact: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      serviceDropdownOpen: false,
      dayWindowStart: 0,
      // Recalculated from strip width; desktop (lg+) locks to visibleDayCount.
      daysPerPage: DEFAULT_MOBILE_DAY_COUNT,
      slots: [],
      loading: false,
      loadError: null,
      showAllPeriods: false,
      serviceDropdownStyle: {},
      // Fallback selection when the panel is used uncontrolled (no v-model),
      // e.g. one panel per doctor card on the /szukaj results list.
      internalServiceUuid: this.modelValue,
      // Empty-calendar lead capture ("Powiadom mnie").
      interestContact: '',
      interestHoneypot: '',
      interestFormStartedAt: Date.now(),
      interestError: null,
      interestSubmitting: false,
      interestSubmitted: false,
    };
  },
  computed: {
    selectedServiceUuid: {
      get() {
        return this.modelValue != null ? this.modelValue : this.internalServiceUuid;
      },
      set(value) {
        this.internalServiceUuid = value;
        this.$emit('update:modelValue', value);
      },
    },
    selectedService() {
      return this.bookableServices.find((service) => service.remote_uuid === this.selectedServiceUuid) || null;
    },
    selectedServiceMeta() {
      return this.selectedService ? this.formatServiceMeta(this.selectedService) : '';
    },
    effectiveDayCount() {
      return this.daysPerPage;
    },
    slotsByDate() {
      return this.slots.reduce((acc, slot) => {
        acc[slot.date] = Array.isArray(slot.periods) ? slot.periods : [];
        return acc;
      }, {});
    },
    // Only days that actually have free slots — empty days are omitted so the
    // calendar never shows blank columns between available dates.
    allDays() {
      const days = [];
      const start = startOfDay(new Date());

      for (let offset = 0; offset < SLOT_DAYS; offset += 1) {
        const date = new Date(start);
        date.setDate(start.getDate() + offset);
        const isoDate = toIsoDate(date);
        if ((this.slotsByDate[isoDate] || []).length === 0) continue;
        days.push(this.buildDayMeta(date, offset));
      }

      return days;
    },
    visibleDays() {
      return this.allDays.slice(this.dayWindowStart, this.dayWindowStart + this.effectiveDayCount);
    },
    canGoBack() {
      return this.dayWindowStart > 0;
    },
    canGoForward() {
      return this.dayWindowStart + this.effectiveDayCount < this.allDays.length;
    },
    hasAnyPeriods() {
      return this.slots.some((slot) => Array.isArray(slot.periods) && slot.periods.length > 0);
    },
    hasMorePeriods() {
      return this.visibleDays.some((day) => this.periodsForDay(day.isoDate).length > INITIAL_PERIOD_COUNT);
    },
  },
  watch: {
    bookableServices: {
      immediate: true,
      handler(services) {
        if (!services.length) {
          this.selectedServiceUuid = null;
          return;
        }

        const defaultUuid = this.resolveDefaultServiceUuid(services);
        if (!this.selectedServiceUuid || !services.some((service) => service.remote_uuid === this.selectedServiceUuid)) {
          this.selectedServiceUuid = defaultUuid;
        }
      },
    },
    selectedServiceUuid: {
      immediate: true,
      handler(serviceUuid) {
        if (serviceUuid) {
          this.dayWindowStart = 0;
          this.showAllPeriods = false;
          this.resetInterestForm();
          this.fetchSlots(serviceUuid);
        }
      },
    },
    loading(isLoading) {
      // Days strip mounts only after slots resolve — measure columns then.
      if (!isLoading) {
        this.$nextTick(() => this.updateDayColumns());
      }
    },
    serviceDropdownOpen(open) {
      if (open) {
        this.$nextTick(this.setupServiceDropdownPosition);
      } else {
        this.teardownServiceDropdownPosition();
      }
    },
  },
  mounted() {
    document.addEventListener('click', this.handleDocumentClick);
    window.addEventListener('resize', this.updateDayColumns);
    this.$nextTick(() => this.updateDayColumns());
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleDocumentClick);
    window.removeEventListener('resize', this.updateDayColumns);
    this.teardownServiceDropdownPosition();
  },
  methods: {
    updateDayColumns() {
      const el = this.$refs.daysScroll;
      if (!el) return;
      // lg+: always visibleDayCount (5). Mobile: fit w-16 columns to width.
      this.daysPerPage = resolveDayColumns(el.clientWidth, {
        min: 1,
        max: this.visibleDayCount,
        desktopCount: this.visibleDayCount,
      });
      const maxStart = Math.max(this.allDays.length - this.effectiveDayCount, 0);
      if (this.dayWindowStart > maxStart) this.dayWindowStart = maxStart;
    },
    setupServiceDropdownPosition() {
      const button = this.$refs.serviceButton;
      const dropdown = this.$refs.serviceDropdown;
      if (!button || !dropdown) return;

      this.serviceDropdownCleanup = autoUpdate(button, dropdown, () => {
        computePosition(button, dropdown, {
          placement: 'bottom-start',
          strategy: 'fixed',
          middleware: [offset(4), flip(), shift({ padding: 8 })],
        }).then(({ x, y }) => {
          this.serviceDropdownStyle = {
            position: 'fixed',
            left: `${x}px`,
            top: `${y}px`,
            width: `${button.offsetWidth}px`,
          };
        });
      });
    },
    teardownServiceDropdownPosition() {
      if (this.serviceDropdownCleanup) {
        this.serviceDropdownCleanup();
        this.serviceDropdownCleanup = null;
      }
    },
    resolveDefaultServiceUuid(services) {
      const defaultUuids = this.defaultServices
        .map((entry) => extractDefaultServiceUuid(entry))
        .filter(Boolean);
      const defaultUuid = defaultUuids.find((uuid) =>
        services.some((service) => service.remote_uuid === uuid),
      );
      return defaultUuid || services[0]?.remote_uuid || null;
    },
    formatServiceMeta(service) {
      return formatServiceMeta(service);
    },
    goToRegistration(isoDate, period) {
      if (!this.selectedServiceUuid) return;

      const query = new URLSearchParams({
        specialist: this.specialistSlug,
        service: this.selectedServiceUuid,
        date: `${isoDate} ${period}`,
      });

      router.visit(`/rejestracja?${query.toString()}`);
    },
    buildDayMeta(date, offsetFromToday) {
      const isoDate = toIsoDate(date);
      let label = this.$t('booking.weekdays_short')[date.getDay()];

      if (offsetFromToday === 0) label = this.$t('booking.today');
      else if (offsetFromToday === 1) label = this.$t('booking.tomorrow');

      return {
        isoDate,
        label,
        dateLabel: `${date.getDate()} ${this.$t('booking.months_short')[date.getMonth()]}`,
      };
    },
    periodsForDay(isoDate) {
      return this.slotsByDate[isoDate] || [];
    },
    scrollToFirstAvailable() {
      // allDays already excludes empty days, so the first column is the nearest
      // available date — just clamp the window start.
      this.dayWindowStart = 0;
    },
    visiblePeriods(isoDate) {
      const periods = this.periodsForDay(isoDate);
      if (this.showAllPeriods) return periods;
      return periods.slice(0, INITIAL_PERIOD_COUNT);
    },
    selectService(remoteUuid) {
      this.selectedServiceUuid = remoteUuid;
      this.serviceDropdownOpen = false;
      this.dayWindowStart = 0;
      this.showAllPeriods = false;
    },
    resetInterestForm() {
      this.interestContact = '';
      this.interestHoneypot = '';
      this.interestFormStartedAt = Date.now();
      this.interestError = null;
      this.interestSubmitting = false;
      this.interestSubmitted = false;
    },
    async submitInterest() {
      const contact = this.interestContact.trim();
      if (!contact) {
        this.interestError = this.$t('booking.interest_contact_required');
        return;
      }

      this.interestSubmitting = true;
      this.interestError = null;

      try {
        const response = await fetch('/api/booking-interests', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            booking_interest: {
              contact,
              specialist_slug: this.specialistSlug,
              service: this.selectedServiceUuid,
              company_website: this.interestHoneypot,
              form_started_at: this.interestFormStartedAt,
            },
          }),
        });

        const payload = await response.json().catch(() => ({}));

        if (!response.ok) {
          const contactErrors = payload.errors?.contact;
          this.interestError = Array.isArray(contactErrors)
            ? contactErrors[0]
            : (payload.error || this.$t('booking.interest_submit_error'));
          return;
        }

        this.interestSubmitted = true;
        this.interestContact = '';
        this.interestHoneypot = '';
      } catch {
        this.interestError = this.$t('booking.interest_submit_error');
      } finally {
        this.interestSubmitting = false;
      }
    },
    shiftDays(delta) {
      const next = this.dayWindowStart + delta;
      const maxStart = Math.max(this.allDays.length - this.effectiveDayCount, 0);
      this.dayWindowStart = Math.min(Math.max(next, 0), maxStart);
      // Keep the "show more hours" state while paging left/right; it only
      // resets when the selected service changes (a genuinely new view).
    },
    handleDocumentClick(event) {
      if (!this.serviceDropdownOpen) return;
      if (this.$refs.serviceSelectRoot?.contains(event.target)) return;
      if (this.$refs.serviceDropdown?.contains(event.target)) return;
      this.serviceDropdownOpen = false;
    },
    async fetchSlots(serviceUuid) {
      this.loading = true;
      this.loadError = null;
      this.slots = [];

      const query = new URLSearchParams({
        service: serviceUuid,
        days: String(SLOT_DAYS),
        start_date: formatStartDate(new Date()),
      });

      try {
        const response = await fetch(
          `/api/specialists/${encodeURIComponent(this.specialistSlug)}/slots?${query.toString()}`,
          {
            headers: { Accept: 'application/json' },
          },
        );

        const payload = await response.json().catch(() => ({}));

        if (!response.ok) {
          this.loadError = payload.error || this.$t('booking.load_error');
          return;
        }

        this.slots = Array.isArray(payload.slots) ? payload.slots : [];
        this.scrollToFirstAvailable();
      } catch {
        this.loadError = this.$t('booking.load_error');
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
